import React from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import {
  RiLoader4Line,
  RiEditLine,
  RiTimeLine,
  RiEmotionSadLine,
  RiDeleteBinLine,
  RiAddCircleLine,
  RiCheckboxCircleLine,
} from 'react-icons/ri';
import entityActivityService, {
  EntityActivityItem,
  EntityActivityType,
} from '../../services/entityActivityService';
import { formatDateTime } from '../../utils/dateUtils';
import { getApiErrorMessage } from '../../utils/mapFieldErrors';
import { normalizeHistoryDateTime } from '../maintenance/ActivityTimeline';

/**
 * ไทม์ไลน์ "ประวัติกิจกรรม" แบบทั่วไปของหน้ารายละเอียด (บริษัท/โครงการ/อุปกรณ์/…)
 * อ่าน GET /activity-logs/entity/{type}/{id} · หน้าตาเดียวกับ components/maintenance/ActivityTimeline
 *
 * - แถวที่มี `details.changes` (บันทึกหลัง 28 ต.ค. 2026) → แสดง ค่าเดิม (ขีดฆ่าแดง) → ค่าใหม่ (เขียว) ต่อฟิลด์
 * - แถวเก่า "Updated company 'X' - fields: a, b" → แสดง "แก้ไข: <ป้ายฟิลด์>" (ไม่มีค่าเก่า/ใหม่ให้โชว์)
 * - action อื่นที่ backend เขียนข้อความไทยเอง → แสดง description ตรง ๆ
 */
export interface EntityActivityTimelineProps {
  entityType: EntityActivityType;
  entityId: number;
  /** ชื่อฟิลด์ภาษาไทย — ฟิลด์ที่ไม่อยู่ในนี้แสดงชื่อดิบ */
  fieldLabels?: Record<string, string>;
  /** แปลงค่าเฉพาะฟิลด์ (เช่น is_active → ใช้งาน/ไม่ใช้งาน) · คืนสตริงที่จะแสดง */
  valueFormatters?: Record<string, (value: unknown) => string>;
  /** เพิ่มค่าเพื่อโหลดใหม่หลังทำ action บนหน้า */
  refreshTrigger?: number;
  pageSize?: number;
}

const ACTION_LABELS: Record<string, string> = {
  CREATE: 'สร้างรายการ',
  UPDATE: 'แก้ไขข้อมูล',
  DELETE: 'ลบรายการ',
  STATUS_CHANGE: 'เปลี่ยนสถานะ',
};

/** ข้อความอังกฤษอัตโนมัติจาก ActivityLogService.log_create/log_update/log_delete — ซ่อน แสดงป้ายไทยแทน */
const BOILERPLATE = /^(Created|Updated|Deleted) [a-z_]+( '.*?')?( - fields: (.+))?$/s;

const ISO_LIKE = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}/;

export const formatActivityValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '' || value === 'None') return '-';
  if (typeof value === 'boolean') return value ? 'ใช่' : 'ไม่ใช่';
  if (typeof value === 'number') return value.toLocaleString('th-TH');
  if (typeof value === 'string') {
    if (ISO_LIKE.test(value)) {
      const f = formatDateTime(normalizeHistoryDateTime(value));
      return f === '-' ? value : f;
    }
    return value;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const truncate = (s: string, max = 60) => (s.length > max ? `${s.slice(0, max)}…` : s);

const ActivityIcon: React.FC<{ action: string }> = ({ action }) => {
  const [bg, fg, Icon] =
    action === 'CREATE' ? ['bg-accent-100', 'text-accent-600', RiAddCircleLine]
    : action === 'UPDATE' ? ['bg-warning-100', 'text-warning-600', RiEditLine]
    : action === 'DELETE' ? ['bg-danger-100', 'text-danger-600', RiDeleteBinLine]
    : action === 'STATUS_CHANGE' ? ['bg-info-100', 'text-info-600', RiCheckboxCircleLine]
    : ['bg-secondary-100', 'text-secondary-600', RiTimeLine];
  return (
    <div className={`flex h-10 w-10 items-center justify-center rounded-full ${bg} ring-8 ring-white`}>
      <Icon className={`h-5 w-5 ${fg}`} />
    </div>
  );
};

export const EntityActivityTimeline: React.FC<EntityActivityTimelineProps> = ({
  entityType, entityId, fieldLabels = {}, valueFormatters = {}, refreshTrigger = 0, pageSize = 20,
}) => {
  const query = useInfiniteQuery({
    queryKey: ['entity-activity', entityType, entityId, refreshTrigger, pageSize],
    queryFn: ({ pageParam }) =>
      entityActivityService.getEntityActivity(entityType, entityId, { page: pageParam, limit: pageSize }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.page * last.limit < last.total ? last.page + 1 : undefined),
    enabled: entityId > 0,
  });

  const label = (field: string) => fieldLabels[field] || field;
  const fmt = (field: string, v: unknown) =>
    v === null || v === undefined || v === '' ? '-' : (valueFormatters[field] ?? formatActivityValue)(v);

  const renderBody = (a: EntityActivityItem) => {
    const changes = a.details?.changes;
    if (changes && Object.keys(changes).length > 0) {
      return (
        <div className="mt-2 space-y-1" data-testid="activity-changes">
          {Object.entries(changes).map(([field, c]) => {
            // ฟิลด์ที่ backend ไม่เก็บค่า (list ซ้อน/ความลับ) ส่ง old=new=null มา = บอกแค่ว่าแก้
            if (c?.old === null && c?.new === null) {
              return (
                <div key={field} className="text-sm text-secondary-600">
                  <span className="font-medium">{label(field)}</span>: <span>มีการแก้ไข</span>
                </div>
              );
            }
            const oldV = fmt(field, c?.old);
            const newV = fmt(field, c?.new);
            return (
              <div key={field} className="text-sm text-secondary-600 [overflow-wrap:anywhere]">
                <span className="font-medium">{label(field)}</span>:
                {oldV === '-' ? (
                  <span className="ml-1">-</span>
                ) : (
                  <span className="ml-1 text-danger-700 line-through" title={oldV}>{truncate(oldV)}</span>
                )}
                <span className="mx-1">→</span>
                <span className="text-success-700" title={newV}>{truncate(newV)}</span>
              </div>
            );
          })}
        </div>
      );
    }
    const m = a.description ? BOILERPLATE.exec(a.description) : null;
    if (m) {
      const fields = m[4]?.split(',').map((f) => f.trim()).filter(Boolean) ?? [];
      return fields.length > 0 ? (
        <p className="mt-1 text-sm text-secondary-600 [overflow-wrap:anywhere]">
          แก้ไข: {fields.map(label).join(', ')}
        </p>
      ) : null;
    }
    return a.description ? (
      <p className="mt-1 text-sm text-secondary-600 whitespace-pre-wrap [overflow-wrap:anywhere]">{a.description}</p>
    ) : null;
  };

  if (query.isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RiLoader4Line className="h-8 w-8 text-primary-500 animate-spin" />
        <p className="ml-2 text-sm text-secondary-500">กำลังโหลดประวัติกิจกรรม...</p>
      </div>
    );
  }

  if (query.isError) {
    return (
      <div className="p-4 bg-danger-50 border border-danger-200 rounded-lg">
        <p className="text-sm text-danger-800">{getApiErrorMessage(query.error, 'ไม่สามารถโหลดประวัติกิจกรรมได้')}</p>
        <button type="button" onClick={() => query.refetch()} className="mt-2 text-sm text-danger-600 hover:text-danger-700 underline">
          ลองอีกครั้ง
        </button>
      </div>
    );
  }

  const items = query.data?.pages.flatMap((p) => p.items) ?? [];
  if (items.length === 0) {
    return (
      <div className="text-center py-8">
        <RiEmotionSadLine className="mx-auto h-12 w-12 text-secondary-300" />
        <p className="mt-2 text-sm text-secondary-500">ยังไม่มีประวัติกิจกรรม</p>
      </div>
    );
  }

  return (
    <div className="flow-root" data-testid="entity-activity-timeline">
      <ul className="-mb-8">
        {items.map((a, idx) => (
          <li key={a.id}>
            <div className="relative pb-8">
              {idx !== items.length - 1 && (
                <span className="absolute left-5 top-5 -ml-px h-full w-0.5 bg-secondary-200" aria-hidden="true" />
              )}
              <div className="relative flex items-start space-x-3">
                <div className="relative"><ActivityIcon action={a.action} /></div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm">
                    <span className="font-medium text-secondary-900">{a.user_name || 'ระบบ'}</span>
                  </div>
                  <p className="mt-0.5 text-sm text-secondary-500">{ACTION_LABELS[a.action] || a.action}</p>
                  {renderBody(a)}
                  <div className="mt-2 text-xs text-secondary-600">{formatDateTime(a.created_at)}</div>
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>
      {query.hasNextPage && (
        <div className="pt-6 text-center">
          <button
            type="button"
            onClick={() => query.fetchNextPage()}
            disabled={query.isFetchingNextPage}
            className="inline-flex items-center px-3 min-h-[44px] sm:min-h-[32px] py-1.5 text-sm font-medium text-secondary-700 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50 disabled:opacity-50"
          >
            {query.isFetchingNextPage ? 'กำลังโหลด...' : 'โหลดเพิ่ม'}
          </button>
        </div>
      )}
    </div>
  );
};

export default EntityActivityTimeline;
